import { Modal, Form, Input, InputNumber, Select, Button, DatePicker } from "antd";
import { useEffect } from "react";

type PaymentModalProps = {
  visible: boolean;
  onCancel: () => void;
  onSave: (values: any) => void;
  initialValues?: any;
  isCreditConversion?: boolean;
  isAuthorizing?: boolean;
  loading?: boolean;
};

const { Option } = Select;

const PaymentModal = ({
  visible,
  onCancel,
  onSave,
  initialValues = {},
  isCreditConversion = false,
  isAuthorizing = false,
  loading = false,
}: PaymentModalProps) => {
  const [form] = Form.useForm();
  const mode = Form.useWatch("mode", form);
  const isPaymentsTabCreditConversion = Boolean(
    isAuthorizing && !isCreditConversion && mode === "credit"
  );

  const primaryActionLabel = isPaymentsTabCreditConversion
    ? "Convert to Credit"
    : isAuthorizing
      ? "Authorize"
      : isCreditConversion
        ? "Convert to Payment"
        : "Save Changes";

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSave(values);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={isAuthorizing ? 'Authorize Payment' : (isCreditConversion ? 'Convert Credit to Payment' : 'Edit Payment')}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading} disabled={loading}>
          {primaryActionLabel}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="leadId"
          label="Lead ID"
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="payment_amount"
          label="Amount"
          rules={[{ required: true, message: 'Please enter amount' }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Enter amount"
            min={0}
            precision={2}
          />
        </Form.Item>

        <Form.Item
          name="mode"
          label="Payment Mode"
          rules={[{ required: true, message: 'Please select payment mode' }]}
        >
          <Select placeholder="Select payment mode">
            <Option value="neft_rtgs_imps">NEFT / RTGS / IMPS</Option>
            <Option value="upi">UPI</Option>
            <Option value="credit">Credit</Option>
            <Option value="cash">Cash</Option>
            <Option value="card">Card</Option>
            <Option value="cheque">Cheque</Option>
            <Option value="razorpay">Razorpay</Option>
            {/* Waive-off is retired; keep it only so older waive-off payments still display and save */}
            {initialValues?.mode === "waive-off" && (
              <Option value="waive-off" disabled>Waive Off</Option>
            )}
          </Select>
        </Form.Item>

        <Form.Item
          name="payment_id"
          label="Payment ID"
          rules={[{ required: true, message: 'Please enter Payment ID' }]}
        >
          <Input placeholder="Enter Payment ID" />
        </Form.Item>

        <Form.Item
          name="medium"
          label="Payment Medium"
        >
          <Select placeholder="Select payment medium" allowClear>
            <Option value="pinelabs">PineLabs</Option>
            <Option value="razorpay">Razorpay</Option>
            <Option value="razorpay_pos">Razorpay POS</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="created_at"
          label="Payment Creation Date"
          rules={[{ required: true, message: 'Please select payment creation date' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Select payment creation date"
            format="YYYY-MM-DD"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PaymentModal;
